/* eslint-disable @nx/enforce-module-boundaries */
import { Consent } from '@axo/form/feature/axo-form-v2/lib/components';
import { useTranslation } from '@axo/shared/i18n';
import { Button } from '@axo/ui-core/components/Button';
import { Grid } from '@axo/ui-core/components/layout/item';
import { Modal } from '@axo/ui-core/components/Modal';
import { Text } from '@axo/ui-core/components/typography';
import { useFormContext } from 'react-hook-form';
import { useAxoFormContext } from '../../../../../../context/AxoForm/AxoForm';
import { useMarketingConsent } from '../../../../../../hooks/useMarketingConsent';
import type { Step7Data } from '../../../FormData.dk.types';
import styles from './ConsentFormGroup.module.scss';

type ConsentFormGroupProps = {
  isModalOpen: boolean;
  onClick: () => void;
  onCloseModal: () => void;
};

export const ConsentFormGroup = ({
  isModalOpen,
  onClick,
  onCloseModal,
}: ConsentFormGroupProps) => {
  const { t } = useTranslation();

  const { setValue } = useFormContext();
  const { axoFormData } = useAxoFormContext<Step7Data>();

  const { showMarketingConsent = true } = useMarketingConsent({
    marketingConsent: axoFormData['Ignore/MarketingConsent'],
    shouldShowMarketingConsent: axoFormData['Ignore/ShowMarketingConsent'],
  });

  const handleAcceptConsent = () => {
    setValue('LoanApplication/CreditStatusConsent', true, {
      shouldTouch: true,
      shouldDirty: true,
    });
    onClick();
  };

  const handleRejectConsent = () => {
    setValue('Frontend/CreditStatusConsentRejected', true);
    onClick();
  };

  return (
    <>
      <Consent>
        <Consent.Credit hasDescriptionArea />
        {/* as Consent parent is injecting Terms as a prop to the first children elements, it needs to be the first child of the Consent component, thus duplicating the showMarketingConsent condition
        ideal would be React Query, but the form is not using it yet*/}
        <Consent.Marketing visible={showMarketingConsent} />
        <Consent.TermsAndConditions visible={showMarketingConsent} />
      </Consent>

      <Modal
        open={isModalOpen}
        onClose={onCloseModal}
        className={styles.consent}
      >
        <Modal.Header feedbackState="warn">
          <Text size="xl">
            <b>{t('Consent.credit.modal.title')}</b>
          </Text>
          <Text>{t('Consent.credit.modal.description')}</Text>
        </Modal.Header>

        <Modal.Actions className={styles.consentActions}>
          <Grid columns={{ s: 1, m: 2, l: 2 }}>
            <Button
              variant="tertiary"
              onClick={handleRejectConsent}
              data-testid="reject-credit-consent-button"
            >
              {t('Consent.credit.modal.reject')}
            </Button>
            <Button
              className={styles.accept}
              onClick={handleAcceptConsent}
              data-testid="accept-credit-consent-button"
            >
              {t('Consent.credit.modal.accept')}
            </Button>
          </Grid>
        </Modal.Actions>
      </Modal>
    </>
  );
};
